import { isDragAndDropItemPinComponent, isDragAndDropPin, PinComponent } from '@lessonup/pins-shared';
import { color, IconDragIndicator } from '@lessonup/ui-components';
import React, { useMemo } from 'react';
import { usePinContext } from '../../contexts/pinContext';
import { TagWrapper } from './TagWrapper';

export type LabelFormat = 'number' | 'char' | undefined;

/**
 * Component with logic that uses TagWrapper for rendering
 */
type EditorTagWrapperProps = {
  style?: React.CSSProperties;
  labelFormat?: LabelFormat;
  id: string;
  children?: React.ReactNode;
  withBorder?: boolean;
};

export const EditorTagWrapper = ({ labelFormat, style, id, children, withBorder = false }: EditorTagWrapperProps) => {
  const pin = usePinContext();
  const pinComponents = pin.pinComponents;

  const tag = useMemo(() => {
    const filteredComponents = pinComponents.filter((component: PinComponent) => {
      if (isDragAndDropPin(pin)) {
        return isDragAndDropItemPinComponent(component, pin.settings.linkedPinComponents);
      }
      return false;
    });

    const componentIndex = filteredComponents.findIndex((d: { id: string }) => d.id === id);

    if (componentIndex === -1) {
      return '1';
    }

    const totalCount = filteredComponents.length;
    const reverseIndex = totalCount - componentIndex;

    switch (labelFormat) {
      case 'char':
        // Convert index to character (0 -> A, 1 -> B, etc.)
        return String.fromCharCode(65 + componentIndex); // 65 is 'A' in ASCII
      case 'number':
        // Return reverse 1-based number (last item = 1, first item = totalCount) for drag items
        return String(reverseIndex);
      default:
        return undefined;
    }
  }, [pin, pinComponents, id, labelFormat]);

  return (
    <TagWrapper
      color={color.utils.focus.stroke}
      label={tag}
      icon={<IconDragIndicator />}
      style={style}
      withBorder={withBorder}
    >
      {children}
    </TagWrapper>
  );
};
